import { useState, useEffect, useMemo } from "react";
import { client, getCategorias, getClientes, getIntermediarios, getUsers } from "../../../utils";

// ─── Umbrales de color (días desde última actividad) ─────────────────────────
const UMBRAL_VERDE = 7;
const UMBRAL_AMARILLO = 30;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const PAIS_CODES = { Chile: "CHI", Perú: "PER", Uruguay: "URU" };
const paisToCode = (pais) => PAIS_CODES[pais] || "CHI";

const norm = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

const diasDesde = (fecha) => {
  if (!fecha) return Infinity;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
};

const colorFromDias = (dias) => {
  if (dias <= UMBRAL_VERDE) return "verde";
  if (dias <= UMBRAL_AMARILLO) return "amarillo";
  return "rojo";
};

const COLOR_PRIORIDAD = { rojo: 3, amarillo: 2, verde: 1 };

const peorColor = (colores) => {
  if (colores.length === 0) return "rojo";
  return colores.reduce((peor, c) => (COLOR_PRIORIDAD[c] > COLOR_PRIORIDAD[peor] ? c : peor), "verde");
};

// ─── Subcategorías (por empresa, solo donde aplica) ──────────────────────────
const has = (empresa, kws) => {
  const u = norm(empresa);
  return kws.some((k) => u.includes(norm(k)));
};

const REGIONALES = ["JETSMART", "SKY", "ARAJET", "LATAM", "LAN", "COPA", "AEROLÍNEAS ARGENTINAS"];
const BANCOS_KW = [
  "BCI",
  "BTG",
  "BANCO FALABELLA",
  "CONSORCIO",
  "ITAÚ",
  "LA ARAUCANA",
  "SANTANDER",
  "SCOTIABANK",
  "BICE",
  "BANCO DE CHILE",
  "BANCO ESTADO",
  "BANCO RIPLEY",
];
const APPS_KW = ["UBER", "DIDI", "BEAT", "CABIFY", "RAPPI"];

// Reglas por categoría (clave normalizada sin acentos)
const SUBCATEGORIA_RULES = {
  AEROLINEAS: (e) => (has(e, REGIONALES) ? "Regionales" : "Internacionales"),
  "BANCA & FINANZAS": (e) => {
    if (norm(e).startsWith("AFP")) return "AFP";
    return has(e, BANCOS_KW) ? "Bancos" : "Financieras";
  },
  MOVILIDAD: (e) => (has(e, APPS_KW) ? "Apps" : "Arriendo"),
};

const matchRule = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_RULES).find(([key]) => norm(key) === n)?.[1];
};

// ─── Merge de categorías (varias categorías DB → una sola visual) ────────────
// Mapea nombre normalizado de categoría DB → { grupo, sub }
const MERGE_CATEGORIAS = {
  "AUTO | B2C | GAMA ALTA": { grupo: "AUTOMOTRIZ", sub: "Gama Alta" },
  "AUTO | B2C | GAMA MEDIA": { grupo: "AUTOMOTRIZ", sub: "Gama Media" },
  "AUTO | B2B": { grupo: "AUTOMOTRIZ", sub: "B2B" },
  "AUTO | ALQUILER": { grupo: "AUTOMOTRIZ", sub: "Alquiler" },
};

const matchMerge = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(MERGE_CATEGORIAS).find(([key]) => norm(key) === n)?.[1];
};

// Orden personalizado
const SUBCATEGORIA_ORDER = {
  AEROLINEAS: ["Regionales", "Internacionales"],
  "BANCA & FINANZAS": ["AFP", "Bancos", "Financieras"],
  AUTOMOTRIZ: ["Gama Alta", "Gama Media", "B2B", "Alquiler"],
};

const getSubOrder = (catNombre) => {
  const n = norm(catNombre);
  return Object.entries(SUBCATEGORIA_ORDER).find(([key]) => norm(key) === n)?.[1];
};

// ─── Fetch últimas actividades por email por cliente ──────────────────────────
const fetchUltimasActividades = async (paisCode) => {
  const fechaLimite = new Date();
  fechaLimite.setDate(fechaLimite.getDate() - 90);

  const PAGE_SIZE = 1000;
  let page = 0;
  const allActividades = [];

  for (;;) {
    const { data: batch } = await client
      .from("crm_actividades")
      .select("id, cliente_id, fecha, crm_actividades_destinatarios(email)")
      .not("cliente_id", "is", null)
      .eq("pais", paisCode)
      .in("tipo", ["MAIL", "MEET", "CALL"])
      .gte("fecha", fechaLimite.toISOString())
      .order("fecha", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (!batch || batch.length === 0) break;
    allActividades.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    page++;
  }

  const resultado = {};
  allActividades.forEach((act) => {
    if (!resultado[act.cliente_id]) resultado[act.cliente_id] = {};
    const entry = resultado[act.cliente_id];
    (act.crm_actividades_destinatarios || []).forEach((d) => {
      const email = d.email?.toLowerCase();
      if (email && (!entry[email] || act.fecha > entry[email])) {
        entry[email] = act.fecha;
      }
    });
  });

  return resultado;
};

// ─── Hook principal ──────────────────────────────────────────────────────────
export const useMapaCalorData = (pais) => {
  const [categorias, setCategorias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [actividades, setActividades] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pais) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const paisCode = paisToCode(pais);
        const [cats, clis, users, interms, acts] = await Promise.all([
          getCategorias(),
          getClientes({ pais }),
          getUsers(),
          getIntermediarios({ pais }).catch(() => []),
          fetchUltimasActividades(paisCode),
        ]);
        if (cancelled) return;
        setCategorias(cats);
        setClientes(clis);
        setUsuarios(users);
        setIntermediarios(interms || []);
        setActividades(acts);
      } catch {
        // Datos parciales se manejan gracefully
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pais]);

  const usuariosMap = useMemo(() => {
    const map = {};
    usuarios.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [usuarios]);

  const intermediariosMap = useMemo(() => {
    const map = {};
    intermediarios.forEach((i) => {
      map[i.id] = i;
    });
    return map;
  }, [intermediarios]);

  const categoriasMap = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categorias]);

  // Mapea ID de categoría → función de subcategoría (resuelto por ID, no por nombre)
  const subRulesById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const rule = matchRule(c.nombre);
      if (rule) map[c.id] = rule;
    });
    return map;
  }, [categorias]);

  // Mapea ID de categoría → merge info { grupo, sub } para fusionar categorías
  const mergeById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const merge = matchMerge(c.nombre);
      if (merge) map[c.id] = merge;
    });
    return map;
  }, [categorias]);

  // Mapea ID de categoría → orden personalizado de subcategorías
  const subOrderById = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      const order = getSubOrder(c.nombre);
      if (order) map[c.id] = order;
    });
    return map;
  }, [categorias]);

  // ─── Construir estructura jerárquica ─────────────────────────────────────
  const { data, maxContactos, subcategoriasDisponibles } = useMemo(() => {
    if (clientes.length === 0 || categorias.length === 0) {
      return { data: [], maxContactos: 1, subcategoriasDisponibles: [] };
    }

    const getContactosConCalor = (cli, tipo) => {
      const fields =
        tipo === "agencia"
          ? [cli.contacto_agencia, cli.contacto_agencia_1, cli.contacto_agencia_2, cli.contacto_agencia_3]
          : [cli.contacto_cliente, cli.contacto_cliente_1, cli.contacto_cliente_2, cli.contacto_cliente_3];

      const actPerEmail = actividades[cli.id] || {};

      return fields
        .filter((id) => id != null)
        .map((id) => usuariosMap[id])
        .filter(Boolean)
        .map((user) => {
          const email = user.email?.toLowerCase();
          const lastDate = email ? actPerEmail[email] || null : null;
          const dias = diasDesde(lastDate);
          return { ...user, dias, color: colorFromDias(dias) };
        });
    };

    const clientesConCalor = clientes.map((cli) => {
      const contactosAgencia = getContactosConCalor(cli, "agencia");
      const contactosCliente = getContactosConCalor(cli, "cliente");
      const allColors = [...contactosAgencia.map((c) => c.color), ...contactosCliente.map((c) => c.color)];

      const agenciaNombre = cli.agencia ? intermediariosMap[cli.agencia]?.nombre || "Agencia" : "Directo";

      const diasAgencia = contactosAgencia.length > 0 ? Math.min(...contactosAgencia.map((c) => c.dias)) : Infinity;
      const diasCliente = contactosCliente.length > 0 ? Math.min(...contactosCliente.map((c) => c.dias)) : Infinity;

      return {
        ...cli,
        contactosAgencia,
        contactosCliente,
        agenciaNombre,
        diasAgencia,
        diasCliente,
        color: peorColor(allColors),
        categoriaNombre: categoriasMap[cli.categoria]?.nombre || "SIN CATEGORÍA",
      };
    });

    // Agrupar por categoría (con merge), luego por marca (empresa) → productos
    const porCategoria = {};
    clientesConCalor.forEach((cli) => {
      const catId = cli.categoria || "sin";
      if (cli.categoriaNombre === "SIN CATEGORÍA") return;

      const merge = mergeById[catId];
      const groupKey = merge ? `merge:${norm(merge.grupo)}` : catId;
      const groupName = merge ? merge.grupo : cli.categoriaNombre;

      if (!porCategoria[groupKey]) {
        porCategoria[groupKey] = {
          id: groupKey,
          nombre: groupName,
          _productos: [],
          _mergedCatIds: merge ? [catId] : [],
        };
      } else if (merge && !porCategoria[groupKey]._mergedCatIds.includes(catId)) {
        porCategoria[groupKey]._mergedCatIds.push(catId);
      }

      // Cada fila de la BD es un producto; guardar con su subcategoría
      const subFromMerge = merge ? merge.sub : null;
      porCategoria[groupKey]._productos.push({ ...cli, _subFromMerge: subFromMerge });
    });

    // Asignar subcategoría, agrupar por marca y construir jerarquía
    const allSubs = new Set();

    Object.values(porCategoria).forEach((cat) => {
      const lista = cat._productos;
      delete cat._productos;
      delete cat._mergedCatIds;

      // Asignar subcategoría a cada producto
      lista.forEach((cli) => {
        if (cli._subFromMerge) {
          cli.subcategoria = cli._subFromMerge;
        } else {
          const rule = subRulesById[cli.categoria];
          cli.subcategoria = rule ? rule(cli.empresa) : "General";
        }
        delete cli._subFromMerge;
        allSubs.add(cli.subcategoria);
      });

      // Agrupar por subcategoría
      const subGroups = {};
      lista.forEach((cli) => {
        if (!subGroups[cli.subcategoria]) subGroups[cli.subcategoria] = [];
        subGroups[cli.subcategoria].push(cli);
      });

      // Para categorías mergeadas, buscar orden por nombre del grupo
      const orderByName = Object.entries(SUBCATEGORIA_ORDER).find(([key]) => norm(key) === norm(cat.nombre))?.[1];
      const customOrder = orderByName || subOrderById[cat.id];

      cat.subcategorias = Object.entries(subGroups)
        .map(([nombre, prods]) => {
          // Dentro de cada subcategoría, agrupar por marca (empresa)
          const marcasMap = {};
          prods.forEach((p) => {
            const marcaKey = (p.empresa || "").toUpperCase().trim();
            if (!marcasMap[marcaKey]) {
              marcasMap[marcaKey] = {
                nombre: p.empresa || "SIN MARCA",
                agenciaNombre: p.agenciaNombre,
                productos: [],
              };
            }
            marcasMap[marcaKey].productos.push(p);
          });

          const marcas = Object.values(marcasMap)
            .map((m) => ({
              ...m,
              productos: m.productos.sort((a, b) => (a.producto || "").localeCompare(b.producto || "")),
              color: peorColor(m.productos.map((p) => p.color)),
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));

          return {
            nombre,
            marcas,
            clientes: prods.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || "")),
            color: peorColor(prods.map((c) => c.color)),
          };
        })
        .sort((a, b) => {
          if (customOrder) {
            const iA = customOrder.indexOf(a.nombre);
            const iB = customOrder.indexOf(b.nombre);
            if (iA !== -1 && iB !== -1) return iA - iB;
            if (iA !== -1) return -1;
            if (iB !== -1) return 1;
          }
          return a.nombre.localeCompare(b.nombre);
        });

      cat.clientes = lista.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || ""));
    });

    // Categorías prioritarias arriba, resto alfabético
    const PRIORIDAD = ["BANCA & FINANZAS", "EDU. | UNIVERSIDADES", "FAST FOOD", "AUTOMOTRIZ"].map(norm);

    const resultado = Object.values(porCategoria)
      .map((cat) => ({
        ...cat,
        color: peorColor(cat.clientes.map((c) => c.color)),
      }))
      .sort((a, b) => {
        const iA = PRIORIDAD.indexOf(norm(a.nombre));
        const iB = PRIORIDAD.indexOf(norm(b.nombre));
        if (iA !== -1 && iB !== -1) return iA - iB;
        if (iA !== -1) return -1;
        if (iB !== -1) return 1;
        return a.nombre.localeCompare(b.nombre);
      });

    return {
      data: resultado,
      maxContactos: 5,
      subcategoriasDisponibles: [...allSubs].sort(),
    };
  }, [
    clientes,
    categorias,
    actividades,
    usuariosMap,
    categoriasMap,
    intermediariosMap,
    subRulesById,
    subOrderById,
    mergeById,
  ]);

  return { data, loading, categorias, maxContactos, subcategoriasDisponibles };
};
